import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import VideoGallery from '@/components/shared/VideoGallery';
import thumbnail_video_blur from '@/assets/imgs/placeholders/thumbnail_video_blur.jpg';
import SafeImg from '@/components/shared/SafeImg';
import SafeBg from '@/components/shared/SafeBg';
import BrokenMedia from '@/components/shared/BrokenMedia';
import LockedMediaMask from '@/components/shared/LockedMediaMask';
import UnlockButton from '@/components/shared/UnlockButton';

/**
 * 編輯模式影片預覽：純縮圖 + 播放圖示視覺，不再 inline 播放。
 * 縮圖由後端生成；處理中時顯示 spinner + 處理中文案。
 */
const EditVideoPreview = ({ thumbnail, isProcessing, onEditClick, onDeleteClick }) => {
  const { t } = useTranslation();

  return (
    <div
      className="position-relative d-flex align-items-center justify-content-center h-100"
      style={{ backgroundColor: '#000' }}
    >
      <button
        className="btn btn-dark position-absolute rounded-pill fs-12"
        style={{ top: '8px', left: '8px', zIndex: 10 }}
        onClick={onEditClick}
      >
        {t('edit')}
      </button>

      {thumbnail && !isProcessing && (
        <img
          src={thumbnail}
          alt="Video thumbnail"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
          }}
        />
      )}

      {isProcessing ? (
        <div
          className="position-absolute top-50 start-50 translate-middle d-flex flex-column align-items-center"
          style={{ pointerEvents: 'none' }}
        >
          <div className="spinner-border text-white mb-1" style={{ width: '1.5rem', height: '1.5rem' }} role="status" />
          <span className="text-white fs-12">{t('video_processing')}</span>
        </div>
      ) : (
        <div
          className="position-absolute top-50 start-50 translate-middle"
          style={{ pointerEvents: 'none' }}
        >
          <div
            className="bg-dark rounded-circle d-flex align-items-center justify-content-center"
            style={{ width: '35px', height: '35px' }}
          >
            <FontAwesomeIcon icon={['fas', 'play']} className="text-white" />
          </div>
        </div>
      )}

      <FontAwesomeIcon
        icon={faCircleXmark}
        className="delete-img position-absolute bg-white rounded-pill"
        onClick={onDeleteClick}
      />
    </div>
  );
};

const PostImages = ({
  contentType,
  oldImages,
  newImages,
  editing,
  onDeleteImage,
  onEditMedia,
  isLocked = false,
  lockType = null,
  unlockPrice = null,
  onUnlockClick = null,
  onSubscribeClick = null,
  disabled = false
}) => {
  const { t } = useTranslation();
  const [lockedImgErrorIds, setLockedImgErrorIds] = useState(new Set());
  // 圖片佈局邏輯
  const getColClass = (index, imageCount) => {
    switch (imageCount) {
      case 1:
        return "col-12";
      case 2:
        return "col-6";
      case 3:
        return index < 2 ? "col-6" : "col-12";
      default:
        return "col-6";
    }
  };


  // 渲染舊圖片（貼文/世界觀通用）
  const renderOldImages = () => {
    if (oldImages.length === 0) return null;

    // 世界觀只顯示第一張
    const displayImages = contentType === 'world' ? [oldImages[0]] : oldImages;
    // 判斷是否為單張圖片
    const isSingleImage = displayImages.length === 1;

    return (
      <>
        {editing ? (
          // 編輯模式：統一使用 post-edit-images (300px/200px)
          <div className="row mt-2 mb-2 mx-0 g-1">
            {displayImages.map((image, index) => (
              <div
                key={image.id}
                className={`${getColClass(index, displayImages.length)} position-relative post-edit-images`}
              >
                {image.type === 1 ? (
                  // 影片預覽（純縮圖；目前 World 不支援影片，僅作為通用回退）
                  <EditVideoPreview
                    thumbnail={image.thumbnail_url}
                    isProcessing={!image.playback_url || (image.stream_status && image.stream_status !== 'ready')}
                    onEditClick={() => onEditMedia?.(index)}
                    onDeleteClick={(e) => onDeleteImage(e, image.id, index)}
                  />
                ) : (
                  // 圖片預覽
                  <SafeBg
                    src={image.url}
                    className="h-100"
                    style={{
                      backgroundSize: isSingleImage ? 'contain' : 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                    }}
                  />
                )}

                {image.type !== 1 && (
                  <>
                    {/* Edit 按鈕 */}
                    <button
                      className="btn btn-dark position-absolute rounded-pill fs-12"
                      style={{ top: '8px', left: '8px', zIndex: 10 }}
                      onClick={() => onEditMedia?.(index)}
                    >
                      {t('edit')}
                    </button>

                    <FontAwesomeIcon
                      icon={faCircleXmark}
                      className="delete-img position-absolute bg-white rounded-pill"
                      onClick={(e) => onDeleteImage(e, image.id, index)}
                    />
                  </>
                )}
              </div>
            ))}
          </div>
        ) : (
          // 非編輯模式：根據鎖定狀態分離顯示
          isLocked ? (
            // 鎖定狀態：顯示模糊圖，不使用 VideoGallery
            <div className="position-relative">
              <div className="row mt-2 mx-0 g-1">
                {displayImages.map((image, index) => {
                  if (index >= 4) return null;
                  return (
                  <div
                    key={image.id}
                    className={`${getColClass(index, Math.min(displayImages.length, 4))} ${isSingleImage ? 'post-single-image' : 'post-multiple-images'} mb-2`}
                  >
                    <div className="position-relative h-100">
                      {isSingleImage ? (
                        image.type === 1 ? (
                          // 影片：靜態模糊圖，不會失效
                          <img src={thumbnail_video_blur} alt="Locked Content" className="w-100" style={{ height: 'auto', maxHeight: '600px', objectFit: 'contain', display: 'block' }} />
                        ) : lockedImgErrorIds.has(image.id) ? (
                          // 圖片失效：明確高度的破圖
                          <BrokenMedia className="w-100" style={{ height: '300px' }} />
                        ) : (
                          <SafeImg
                            src={image.thumbnail_url}
                            alt="Locked Content"
                            className="w-100"
                            style={{ height: 'auto', maxHeight: '600px', objectFit: 'contain', display: 'block' }}
                            onError={() => setLockedImgErrorIds(prev => new Set([...prev, image.id]))}
                          />
                        )
                      ) : (
                        // 多圖：SafeImg + object-fit cover，h-100 chain 有 post-multiple-images 300px 支撐
                        lockedImgErrorIds.has(image.id) ? (
                          <BrokenMedia className="w-100 h-100" />
                        ) : (
                          <SafeImg
                            src={image.type === 1 ? thumbnail_video_blur : image.thumbnail_url}
                            alt="Locked Content"
                            onError={image.type !== 1 ? () => setLockedImgErrorIds(prev => new Set([...prev, image.id])) : undefined}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }}
                          />
                        )
                      )}
                      {/* +N 遮罩（媒體>4特有，在黑色遮罩之下） */}
                      {displayImages.length > 4 && index === 3 && (
                        <div className="plus-img position-absolute text-light h-100 w-100">
                          +{displayImages.length - 4}
                        </div>
                      )}
                      {/* 黑色20%遮罩 ＋ 右上角媒體類型icon（lock特有） */}
                      <LockedMediaMask mediaType={image.type} />
                    </div>
                  </div>
                  );
                })}
              </div>
              {/* 解鎖按鈕 - 壓在整組媒體中央 */}
              <div className="locked-media-action position-absolute top-50 start-50 translate-middle">
                <UnlockButton
                  lockType={lockType}
                  unlockPrice={unlockPrice}
                  onUnlockClick={onUnlockClick}
                  onSubscribeClick={onSubscribeClick}
                />
              </div>
            </div>
          ) : (
            // 非鎖定狀態：使用 VideoGallery 正常顯示
            <VideoGallery images={displayImages} disabled={disabled} />
          )
        )}
      </>
    );
  };

  // 渲染新圖片（貼文/世界觀通用，僅編輯模式）
  const renderNewImages = () => {
    if (newImages.length === 0 || !editing) return null;

    // 世界觀只顯示第一張
    const displayImages = contentType === 'world' ? [newImages[0]] : newImages;
    // 判斷是否為單張圖片
    const isSingleImage = displayImages.length === 1;

    return (
      <div className="row mt-2 mb-2 mx-0 g-1">
        {displayImages.map((image, index) => (
          <div
            key={index}
            className={`${getColClass(index, displayImages.length)} position-relative post-edit-images`}
          >
            {image.type === 1 ? (
              // 影片預覽（純縮圖；目前 World 不支援影片，僅作為通用回退）
              <EditVideoPreview
                thumbnail={image.thumbnail_url}
                isProcessing={true}
                onEditClick={() => onEditMedia?.(index + oldImages.length)}
                onDeleteClick={(e) => onDeleteImage(e, undefined, index)}
              />
            ) : (
              // 圖片預覽
              <SafeBg
                src={image.url}
                className="h-100"
                style={{
                  backgroundSize: isSingleImage ? 'contain' : 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                }}
              />
            )}

            {image.type !== 1 && (
              <>
                {/* Edit 按鈕 */}
                <button
                  className="btn btn-dark position-absolute rounded-pill fs-12"
                  style={{ top: '8px', left: '8px', zIndex: 10 }}
                  onClick={() => onEditMedia?.(index + oldImages.length)}
                >
                  {t('edit')}
                </button>

                <FontAwesomeIcon
                  icon={faCircleXmark}
                  className="delete-img position-absolute bg-white rounded-pill"
                  onClick={(e) => onDeleteImage(e, undefined, index)}
                />
              </>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      {/* 渲染圖片（貼文/世界觀通用） */}
      {renderOldImages()}
      {renderNewImages()}
    </>
  );
};

export default PostImages;